// src/pages/HomePage.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence, useScroll, useTransform, MotionConfig } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useProductData } from "../context/ProductDataContext";
import { FUNCTION_LIST } from "../lib/constants";
import { canon } from "../lib/seo";

/* -------------------- helpers -------------------- */
const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(!!mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
};

const variantsFade = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.06 * i, duration: 0.5, ease: "easeOut" },
  }),
};

const springTap = { scale: 0.98 };
const springHover = { y: -2, transition: { type: "spring", stiffness: 400, damping: 24 } };

export default function HomePage() {
  const navigate = useNavigate();
  const { products, categories, loading } = useProductData();

  const [isMobile, setIsMobile] = useState(false);
  const [newsletterOk, setNewsletterOk] = useState(false);
  const prefersReduced = usePrefersReducedMotion();

  // ————— layout effects
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    let t;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(checkMobile, 120);
    };
    checkMobile();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ————— derive data (ajánlott: raktáron lévő, különben első termékek)
  const recommendedProducts = useMemo(() => {
    if (!Array.isArray(products) || !products.length) return [];
    const inStock = products.filter((p) => p?.stock?.inStock);
    const source = inStock.length ? inStock : products;
    return source.slice(0, 12);
  }, [products]);

  const topCategories = useMemo(() => {
    const leaves = [];
    const walk = (arr = []) =>
      arr.forEach((c) => {
        if (Array.isArray(c.children) && c.children.length) walk(c.children);
        else leaves.push(c);
      });
    walk(categories || []);
    return leaves.slice(0, 8);
  }, [categories]);

  // HERO parallax (hydrate-safe)
  const heroRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // csak mount után adunk targetet → nincs "defined but not hydrated"
  const scrollOpts = mounted
    ? { target: heroRef, offset: ["start start", "end start"] }
    : {}; // üres → viewport scroll (safe)

  const { scrollYProgress } = useScroll();
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

  // ————— 3D-ish tilt for cards (perf-friendly, tiny angles)
  const makeTilt = () => {
    // returns onMouseMove, onMouseLeave handlers to set CSS vars
    const handler = (e) => {
      const el = e.currentTarget;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / (rect.width / 2);
      const dy = (e.clientY - cy) / (rect.height / 2);
      el.style.setProperty("--tiltX", `${(-dy * 4).toFixed(2)}deg`);
      el.style.setProperty("--tiltY", `${(dx * 6).toFixed(2)}deg`);
    };
    const leave = (e) => {
      const el = e.currentTarget;
      el.style.setProperty("--tiltX", "0deg");
      el.style.setProperty("--tiltY", "0deg");
    };
    return { onMouseMove: handler, onMouseLeave: leave };
  };

  const onNewsletterSubmit = useCallback((e) => {
    e.preventDefault();
    setNewsletterOk(true);
    const f = e.currentTarget;
    f.reset?.();
    setTimeout(() => setNewsletterOk(false), 2500);
  }, []);

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-64 rounded-xl bg-gray-100" />
          <div className="h-64 rounded-xl bg-gray-100" />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* === SEO – Home === */}
      <title>Solarya – Okos világítás és okosotthon egyszerűen</title>
      <meta
        name="description"
        content="Okos izzók, LED szalagok, szenzorok, hubok és teljes okosotthon megoldások szakértői ajánlásokkal. Böngéssz kategóriák szerint, vagy használd az Okosotthon Buildert."
      />
      <link rel="canonical" href={canon('/')} />

      {/* Open Graph / Twitter */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Solarya – Okos világítás és okosotthon" />
      <meta
        property="og:description"
        content="Okos izzók, LED szalagok, szenzorok, hubok és teljes okosotthon megoldások szakértői ajánlásokkal."
      />
      <meta property="og:image" content="/img/t5.webp" />
      <meta
        property="og:url"
        content={typeof window !== "undefined" ? window.location.href : ""}
      />
      <meta name="twitter:card" content="summary_large_image" />

      {/* Organization JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Solarya",
          url: typeof window !== "undefined" ? window.location.origin : "",
          logo: "/img/solarya-icon-transparent.webp",
        })}
      </script>

      <MotionConfig reducedMotion={prefersReduced ? "always" : "never"}>
        <div className="flex flex-col gap-16">

          {/* =================== HERO =================== */}
          <motion.section className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-[#0c1a26] to-[#173047]" />
            <motion.div style={{ y: bgY }} className="absolute inset-0 opacity-25 will-change-transform">
              <div
                className="w-full h-full"
                style={{
                  backgroundImage: `url(${isMobile ? "/img/borito-mobile.webp" : "/img/t5.webp"})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
            </motion.div>

            <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 text-white">
              <motion.h1
                className="text-3xl md:text-6xl font-extrabold leading-tight drop-shadow-sm"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Okos világítás és otthon, egyszerűen.
              </motion.h1>

              <motion.p
                className="mt-4 text-lg md:text-xl max-w-2xl text-white/90"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.18 }}
              >
                Válaszd ki a szükséges funkciókat, nézd át a termékeket, és rakd össze a saját rendszered – szakértői ajánlásokkal.
              </motion.p>

              <motion.div
                className="mt-8 flex flex-wrap gap-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.34 }}
              >
                <motion.div whileHover={{ y: -2 }} whileTap={springTap}>
                  <Link
                    to="/kategoriak"
                    className="inline-flex items-center justify-center rounded-xl bg-blue-600 hover:bg-blue-700 px-5 py-3 font-semibold"
                  >
                    Termékek böngészése
                  </Link>
                </motion.div>
                <motion.div whileHover={{ y: -2 }} whileTap={springTap}>
                  <Link
                    to="/builder"
                    className="inline-flex items-center justify-center rounded-xl border border-white/40 hover:bg-white hover:text-[#0c1a26] px-5 py-3 font-semibold"
                  >
                    Okosotthon Builder
                  </Link>
                </motion.div>
              </motion.div>

              {/* Value props (glass cards) */}
              <motion.div
                className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
              >
                {/*
                { icon: "📍", title: "Showroom", sub: "Veresegyház, Szent Erzsébet krt." },
              */}
                {[
                  { icon: "⚡", title: "Gyors rendelés", sub: "Átlátható, egyszerű checkout" },
                  { icon: "🧠", title: "Szakértelem", sub: "Hub-kompatibilitás és funkció-szűrők" },
                ].map((v, i) => (
                  <motion.div
                    key={v.title}
                    custom={i}
                    variants={variantsFade}
                    whileHover={springHover}
                    className="relative backdrop-blur bg-white/10 border border-white/20 rounded-2xl p-4"
                  >
                    <div className="text-2xl">{v.icon}</div>
                    <div className="mt-1 font-semibold">{v.title}</div>
                    <div className="text-sm text-white/80">{v.sub}</div>
                    <div className="absolute inset-0 rounded-2xl pointer-events-none shadow-[inset_0_-30px_60px_-30px_rgba(255,255,255,0.25)]" />
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.section>

          {/* =================== CATEGORIES =================== */}
          <section className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between mb-4">
              <h2 className="text-2xl md:text-3xl font-bold">Fedezd fel kategóriáinkat</h2>
              <Link to="/kategoriak" className="text-blue-700 hover:underline text-sm font-medium">
                Összes kategória →
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {topCategories.map((cat, i) => {
                const tilt = makeTilt();
                return (
                  <motion.button
                    key={cat.id}
                    onClick={() => navigate(`/kategoriak/${cat.id}`)}
                    className="group relative rounded-2xl overflow-hidden bg-gray-100 border will-change-transform"
                    custom={i}
                    variants={variantsFade}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-80px" }}
                    style={{
                      transform: "perspective(800px) rotateX(var(--tiltX, 0deg)) rotateY(var(--tiltY, 0deg))",
                      transition: "transform 120ms ease",
                    }}
                    {...tilt}
                  >
                    <img
                      src={cat.image || "/img/placeholder.webp"}
                      alt={cat.name}
                      className="h-44 w-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                      <div className="font-semibold">{cat.name}</div>
                      <div className="text-xs text-white/80">Megnézem</div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </section>

          {/* =================== FUNCTION TEASER =================== */}
          <section className="max-w-7xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Mit szeretnél megvalósítani?</h2>
            <p className="text-gray-600 mb-6">
              Funkciók szerint is böngészhetsz – egy kattintás és már szűrjük is a releváns termékeket.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {FUNCTION_LIST.slice(0, 12).map((f, i) => (
                <motion.div
                  key={f.id}
                  custom={i}
                  variants={variantsFade}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-80px" }}
                  whileHover={{ y: -3 }}
                  className="bg-white border rounded-xl p-3 hover:shadow-lg transition will-change-transform"
                >
                  <button
                    onClick={() => navigate(`/kategoriak?all=1&functions=${encodeURIComponent(f.slug)}`)}
                    className="w-full text-left"
                    aria-label={`${f.title} – termékek`}
                  >
                    <div className="aspect-[4/3] rounded-lg overflow-hidden bg-gray-50 mb-2">
                      <img
                        src={`/img/base/${f.slug}.webp`}
                        onError={(e) => (e.currentTarget.src = "/img/placeholder.webp")}
                        alt={f.title}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    </div>
                    <div className="text-sm font-semibold leading-snug">{f.title}</div>
                    <div className="text-xs text-gray-600 line-clamp-2">{f.desc}</div>
                  </button>
                </motion.div>
              ))}
            </div>

            <div className="mt-4">
              <motion.span whileHover={{ x: 2 }}>
                <Link to="/funkciok" className="text-blue-700 hover:underline text-sm font-medium">
                  Összes funkció →
                </Link>
              </motion.span>
            </div>
          </section>

          {/* =================== RECOMMENDED =================== */}
          <section className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between mb-4">
              <h2 className="text-2xl md:text-3xl font-bold">Ajánlott most</h2>
              <Link to="/kategoriak" className="text-blue-700 hover:underline text-sm font-medium">
                Böngészés →
              </Link>
            </div>

            <div className="overflow-x-auto no-scrollbar">
              <div className="flex gap-4 w-max">
                {recommendedProducts.map((p, i) => {
                  const img = p?.image || "/img/placeholder.webp";
                  const price =
                    typeof p.price === "number" ? `${p.price.toLocaleString("hu-HU")} Ft` : "—";

                  return (
                    <motion.div
                      key={p.id}
                      custom={i}
                      variants={variantsFade}
                      initial="hidden"
                      whileInView="show"
                      viewport={{ once: true, margin: "-80px" }}
                      whileHover={{ y: -3 }}
                      className="min-w-[180px] max-w-[180px] bg-white border rounded-2xl p-3 hover:shadow-lg transition"
                    >
                      <Link to={`/termek/${p.id}`} aria-label={`Megnyitás: ${p.name}`}>
                        <div className="aspect-square rounded-lg overflow-hidden bg-gray-50 mb-2">
                          <img src={img} alt={p.name} className="w-full h-full object-contain" loading="lazy" />
                        </div>
                        <div className="text-sm font-semibold line-clamp-2 min-h-[2.6em]">{p.name}</div>
                        <div className="text-xs text-gray-600 line-clamp-2">{p.subtitle || p.description}</div>
                        <div className="mt-1 font-semibold">{price}</div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* =================== TRUST BAND =================== */}
          <section className="max-w-7xl mx-auto px-6">
            <motion.div
              className="rounded-2xl border bg-white p-6 md:p-8"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
                <div className="text-center md:text-left">
                  <div className="text-xl md:text-2xl font-bold">Megbízható márkák</div>
                  <div className="text-gray-600">Kompatibilitás és stabil működés, amire számíthatsz.</div>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 md:gap-6 w-full">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <motion.div
                      key={i}
                      whileHover={{ scale: 1.04 }}
                      className="h-12 rounded-xl bg-gray-100 border flex items-center justify-center text-gray-400"
                    >
                      LOGO
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </section>

          {/* =================== NEWSLETTER =================== */}
          <section className="max-w-7xl mx-auto px-6">
            <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-blue-50 to-emerald-50 p-6 md:p-10">
              <div className="max-w-xl">
                <h3 className="text-xl md:text-2xl font-bold">Iratkozz fel – tippek & akciók</h3>
                <p className="text-gray-700 mt-1 mb-4">Ritka, hasznos levelek. Nincs spam, bármikor leiratkozhatsz.</p>

                <form onSubmit={onNewsletterSubmit} className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="email"
                    required
                    placeholder="E-mail címed"
                    className="flex-1 border rounded-xl px-4 py-3"
                  />
                  <motion.button
                    type="submit"
                    whileHover={{ y: -2 }}
                    whileTap={springTap}
                    className="rounded-xl bg-[#0c1a26] text-white px-5 py-3 hover:bg-black/90"
                  >
                    Feliratkozom
                  </motion.button>
                </form>

                <AnimatePresence>
                  {newsletterOk && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      className="mt-3 inline-flex items-center gap-2 text-emerald-700 text-sm font-medium"
                    >
                      <span>✅</span> Köszi! Hamarosan küldünk egy megerősítő e-mailt. (placeholder)
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="absolute -right-10 -bottom-10 w-64 h-64 rounded-full bg-white/50 blur-3xl pointer-events-none" />
            </div>
          </section>

          {/* =================== SHOWROOM / MAP =================== */}
          <section className="max-w-7xl mx-auto px-6 mb-8">
            <div className="grid md:grid-cols-2 gap-6 items-stretch">
              <motion.div
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5 }}
                className="rounded-2xl overflow-hidden border"
              >
                {/* Map placeholder – csere éles Google Maps iframe-re */}
                <img
                  src="/img/placeholder.webp"
                  alt="Térkép placeholder"
                  className="w-full h-64 md:h-full object-cover"
                  loading="lazy"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5 }}
                className="flex flex-col justify-center"
              >
                <h3 className="text-2xl font-bold">Látogass meg minket!</h3>
                <p className="text-gray-700 mt-2">
                  Veresegyház • Szent Erzsébet körút — Élő bemutatók, gyors tanácsadás és helyszíni kipróbálás.
                </p>
                <div className="mt-4 flex gap-3">
                  <motion.div whileHover={{ y: -2 }} whileTap={springTap}>
                    <Link to="/kategoriak" className="rounded-xl bg-blue-600 text-white px-5 py-3 hover:bg-blue-700">
                      Termékek
                    </Link>
                  </motion.div>
                  <motion.div whileHover={{ y: -2 }} whileTap={springTap}>
                    <Link to="/funkciok" className="rounded-xl border px-5 py-3 hover:bg-gray-50">
                      Funkciók
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>
        </div>
      </MotionConfig>
    </>
  );
}
